<template>
  <div>
    <div v-if="reactions.length" class="mt-1.5 flex flex-wrap items-center gap-1">
      <button
        v-for="reaction in reactions"
        :key="reaction.emoji"
        type="button"
        class="inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-xs transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        :class="
          reactedByMe(reaction)
            ? 'border-hazel-300 bg-hazel-100 text-obsidian-900'
            : 'border-obsidian-200 bg-white text-obsidian-700 hover:bg-hazel-50'
        "
        :title="reactionTitle(reaction)"
        :aria-label="reactionTitle(reaction)"
        :aria-pressed="reactedByMe(reaction)"
        @click="emit('toggle', reaction.emoji)"
      >
        <CommentReactionGlyph :value="reaction.emoji" />
        <span class="tabular-nums">{{ reaction.users.length }}</span>
      </button>

      <CVButton
        type="button"
        variant="ghost"
        size="iconXs"
        class="text-obsidian-400 hover:text-obsidian-800"
        :aria-label="t('add')"
        :title="t('add')"
        :aria-expanded="open"
        @click.stop="togglePicker"
      >
        <SmilePlusIcon class="size-4" />
      </CVButton>
    </div>

    <Teleport :to="teleportTarget">
      <div
        v-if="open"
        ref="floatingRef"
        data-comments-overlay
        :style="floatingStyles"
        class="z-[10050] w-64 rounded-md border border-obsidian-200 bg-white p-2 shadow-lg"
        @click.stop
      >
        <div class="mb-2 grid grid-cols-6 gap-0.5">
          <button
            v-for="emoji in quickEmojis"
            :key="emoji"
            type="button"
            class="flex aspect-square w-full min-w-0 items-center justify-center rounded-sm text-lg transition hover:bg-hazel-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            :aria-label="emoji"
            :title="emoji"
            @click="pick(emoji)"
          >
            <CommentReactionGlyph :value="emoji" />
          </button>
        </div>
        <input
          ref="searchRef"
          v-model="query"
          type="search"
          class="mb-2 w-full rounded-sm border border-obsidian-200 px-2 py-1 text-sm text-obsidian-800 placeholder:text-obsidian-400 focus:border-obsidian-400 focus:outline-none focus:ring-1 focus:ring-ring"
          :placeholder="t('search')"
        />
        <div class="grid max-h-40 grid-cols-8 gap-0.5 overflow-y-auto overflow-x-hidden">
          <button
            v-for="item in filteredEmojis"
            :key="item.key"
            type="button"
            class="flex aspect-square w-full min-w-0 items-center justify-center overflow-hidden rounded-sm text-base transition hover:bg-hazel-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            :title="item.label"
            :aria-label="item.name"
            @click="pick(item.key)"
          >
            <CommentReactionGlyph :value="item.key" />
          </button>
        </div>
        <p v-if="!filteredEmojis.length" class="px-1 py-2 text-xs text-obsidian-500">
          {{ t('noMatches') }}
        </p>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { SmilePlusIcon } from 'lucide-vue-next';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/vue';
import { onClickOutside } from '@vueuse/core';
import { emojis } from '@tiptap/extension-emoji';
import CVButton from '@/components/buttons/CVButton.vue';
import type { CommentReaction } from '@/api/comments.service';
import { customEmojis } from './comment-custom-emojis';
import CommentReactionGlyph from './CommentReactionGlyph.vue';

const QUICK_EMOJIS = ['👍', '👎', '❤️', '😂', '🎉', '🙏'] as const;
const SEARCH_LIMIT = 64;

type PickerItem = {
  key: string;
  label: string;
  name: string;
};

/** Skip letter/flag fillers that dominate the start of the tip tap emoji list. */
const isUsefulUnicode = (name: string, emoji: string | undefined): emoji is string =>
  Boolean(emoji) && !name.startsWith('regional_indicator_');

const filterEmojis = (query: string): PickerItem[] => {
  const q = query.trim().toLowerCase();
  const customMatches = customEmojis
    .filter((item) => !q || item.name.includes(q) || item.shortcode.includes(q))
    .map(
      (item): PickerItem => ({
        key: item.shortcode,
        label: item.shortcode,
        name: item.name,
      }),
    );

  const unicode: PickerItem[] = [];
  const pushUnicode = (name: string, emoji: string) => {
    unicode.push({ key: emoji, label: `:${name}:`, name });
  };

  if (!q) {
    for (const item of emojis) {
      if (!isUsefulUnicode(item.name, item.emoji)) continue;
      pushUnicode(item.name, item.emoji);
      if (unicode.length >= SEARCH_LIMIT) break;
    }
    return [...customMatches, ...unicode].slice(0, SEARCH_LIMIT);
  }

  const prefix: PickerItem[] = [];
  const rest: PickerItem[] = [];
  for (const item of emojis) {
    if (!isUsefulUnicode(item.name, item.emoji)) continue;
    const entry: PickerItem = { key: item.emoji, label: `:${item.name}:`, name: item.name };
    if (item.name.startsWith(q) || item.shortcodes.some((code) => code.startsWith(q))) {
      prefix.push(entry);
    } else if (
      item.name.includes(q) ||
      item.shortcodes.some((code) => code.includes(q)) ||
      item.tags.some((tag) => tag.startsWith(q) || tag.includes(q))
    ) {
      rest.push(entry);
    }
  }
  return [...customMatches, ...prefix, ...rest].slice(0, SEARCH_LIMIT);
};

const props = defineProps<{
  meId?: string | null;
  reactions: CommentReaction[];
}>();

const emit = defineEmits<{
  toggle: [emoji: string];
}>();

const { t } = useI18n();
const open = ref(false);
const query = ref('');
const referenceEl = ref<HTMLElement | null>(null);
const floatingRef = ref<HTMLElement | null>(null);
const searchRef = ref<HTMLInputElement | null>(null);
/** Stay inside vue-final-modal's focus trap when comments are in a modal. */
const teleportTarget = ref<HTMLElement | string>('body');

const quickEmojis = QUICK_EMOJIS;
const { floatingStyles } = useFloating(referenceEl, floatingRef, {
  placement: 'top-start',
  strategy: 'fixed',
  whileElementsMounted: autoUpdate,
  middleware: [offset(6), flip(), shift({ padding: 8 })],
});

watch(open, async (isOpen) => {
  if (!isOpen) {
    query.value = '';
    return;
  }
  // Wait for Teleport + floating UI to mount into the modal content.
  await nextTick();
  await nextTick();
  searchRef.value?.focus();
});

onClickOutside(
  floatingRef,
  () => {
    open.value = false;
  },
  { ignore: [referenceEl] },
);

const filteredEmojis = computed((): PickerItem[] => filterEmojis(query.value));

const reactedByMe = (reaction: CommentReaction) =>
  Boolean(props.meId && reaction.users.some((user) => user.id === props.meId));

const reactionTitle = (reaction: CommentReaction) =>
  reaction.users.map((user) => `${user.firstName} ${user.lastName}`.trim()).join(', ');

const togglePicker = (event?: Event) => {
  const target = event?.currentTarget;
  if (target instanceof HTMLElement) {
    referenceEl.value = target;
    // Focus trap lives on `.vfm`; body teleports can't receive keystrokes.
    const modal =
      target.closest<HTMLElement>('.vfm__content') ?? target.closest<HTMLElement>('.vfm');
    teleportTarget.value = modal ?? 'body';
  }
  open.value = !open.value;
};

const pick = (emoji: string) => {
  open.value = false;
  emit('toggle', emoji);
};

defineExpose({ togglePicker });
</script>

<i18n lang="json">
{
  "en": {
    "add": "Add reaction",
    "search": "Search emoji",
    "noMatches": "No emoji found"
  },
  "de": {
    "add": "Reaktion hinzufügen",
    "search": "Emoji suchen",
    "noMatches": "Kein Emoji gefunden"
  },
  "es": {
    "add": "Añadir reacción",
    "search": "Buscar emoji",
    "noMatches": "No se encontró ningún emoji"
  }
}
</i18n>
